import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import type { ModuleCatalog, ModuleDef } from '../types';

interface Props {
  catalog: ModuleCatalog | null;
  disabled?: boolean;
  /** When true, the palette is rendered as a thin rail (icon-only). */
  collapsed?: boolean;
  /** Toggle collapse/expand. The rail's chevron and the expanded header's
   *  collapse button both call this. */
  onToggleCollapsed?: () => void;
  /** Finger lifted after a touch drag; screen coordinates of the drop. */
  onTouchDrop?: (moduleId: number, clientX: number, clientY: number) => void;
}

const LONG_PRESS_MS = 220;
const SCROLL_SLOP_PX = 8;

// Drag source for the React Flow canvas. The canvas reads
// `application/x-flowbuilder-module` (= moduleId as string) on drop.
export function Palette({
  catalog,
  disabled = false,
  collapsed = false,
  onToggleCollapsed,
  onTouchDrop,
}: Props) {
  const [q, setQ] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const touchDrop = useRef(onTouchDrop);
  touchDrop.current = onTouchDrop;
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  // HTML5 drag and drop does not exist on touch screens, so items also
  // support a long press: hold an item, then drag a floating ghost onto
  // the canvas. A finger that moves before the press timer fires is a
  // scroll and is left alone. Native listeners because touchmove must be
  // non-passive to stop the page from scrolling during the drag. The
  // palette stays mounted during the drag (touch events keep targeting the
  // element the finger started on); on phones it is only hidden visually
  // via `fb-palette--dragging` so the canvas underneath is visible.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    let timer: number | null = null;
    let moduleId = 0;
    let label = '';
    let startX = 0;
    let startY = 0;
    let dragging = false;
    let ghost: HTMLDivElement | null = null;

    const cleanup = () => {
      if (timer != null) window.clearTimeout(timer);
      timer = null;
      dragging = false;
      moduleId = 0;
      ghost?.remove();
      ghost = null;
      root.classList.remove('fb-palette--dragging');
    };
    const moveGhost = (x: number, y: number) => {
      if (!ghost) return;
      ghost.style.transform = `translate(${x - 20}px, ${y - 20}px)`;
    };

    const onStart = (e: TouchEvent) => {
      if (disabledRef.current || e.touches.length !== 1) return;
      const item = (e.target as HTMLElement).closest<HTMLElement>('.fb-palette-item');
      if (!item) return;
      moduleId = Number(item.dataset.moduleId ?? 0);
      label = item.dataset.moduleName ?? '';
      if (!moduleId) return;
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      timer = window.setTimeout(() => {
        timer = null;
        dragging = true;
        ghost = document.createElement('div');
        ghost.className = 'fb-touch-ghost';
        ghost.textContent = label;
        document.body.appendChild(ghost);
        moveGhost(startX, startY);
        root.classList.add('fb-palette--dragging');
      }, LONG_PRESS_MS);
    };
    const onMove = (e: TouchEvent) => {
      if (!moduleId) return;
      const t = e.touches[0];
      if (dragging) {
        e.preventDefault();
        moveGhost(t.clientX, t.clientY);
        return;
      }
      if (Math.abs(t.clientX - startX) > SCROLL_SLOP_PX || Math.abs(t.clientY - startY) > SCROLL_SLOP_PX) cleanup();
    };
    const onEnd = (e: TouchEvent) => {
      if (dragging && moduleId) {
        const t = e.changedTouches[0];
        touchDrop.current?.(moduleId, t.clientX, t.clientY);
      }
      cleanup();
    };

    root.addEventListener('touchstart', onStart, { passive: true });
    root.addEventListener('touchmove', onMove, { passive: false });
    root.addEventListener('touchend', onEnd);
    root.addEventListener('touchcancel', cleanup);
    return () => {
      root.removeEventListener('touchstart', onStart);
      root.removeEventListener('touchmove', onMove);
      root.removeEventListener('touchend', onEnd);
      root.removeEventListener('touchcancel', cleanup);
      cleanup();
    };
  }, [catalog, collapsed]);

  const grouped = useMemo(() => {
    if (!catalog) return [];
    const matches = (m: ModuleDef) =>
      !q ||
      m.name?.toLowerCase().includes(q.toLowerCase()) ||
      m.description?.toLowerCase().includes(q.toLowerCase());
    return catalog.categories.map((cat) => {
      const mods = catalog.modules.filter(
        (m) => m.categoryId === cat.categoryId && matches(m)
      );
      return { cat, mods };
    }).filter((g) => g.mods.length > 0);
  }, [catalog, q]);

  const onDragStart = (e: DragEvent<HTMLDivElement>, mod: ModuleDef) => {
    e.dataTransfer.setData('application/x-flowbuilder-module', String(mod.moduleId));
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Collapsed rail: a thin column with just the expand chevron + a stack of
  // module-color dots so users can still get a sense of which modules are
  // available without taking up canvas space.
  if (collapsed) {
    return (
      <div className="fb-palette fb-palette--rail" aria-label="Modules palette (collapsed)">
        <button
          type="button"
          className="fb-rail-toggle"
          onClick={onToggleCollapsed}
          title="Expand modules palette"
          aria-label="Expand modules palette"
        >
          <span className="fb-rail-chevron" aria-hidden="true">›</span>
          <span className="fb-rail-label">Modules</span>
        </button>
      </div>
    );
  }

  if (!catalog) {
    return (
      <div className="fb-palette">
        <PaletteHeader onCollapse={onToggleCollapsed} />
        <div className="fb-palette-empty">Loading modules…</div>
      </div>
    );
  }

  return (
    <div className={`fb-palette${disabled ? ' fb-palette--disabled' : ''}`} ref={rootRef}>
      <PaletteHeader onCollapse={onToggleCollapsed} />
      {disabled && (
        <div className="fb-palette-readonly" title="Read-only view — switch to draft to edit">
          Read-only
        </div>
      )}
      <input
        className="fb-palette-search"
        placeholder="Search modules…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {grouped.length === 0 && (
        <div className="fb-palette-empty">No matches</div>
      )}
      {grouped.map(({ cat, mods }) => (
        <div className="fb-palette-cat" key={cat.categoryId}>
          <div className="fb-palette-cat-lbl">{cat.name}</div>
          {mods.map((mod) => (
            <div
              key={mod.moduleId}
              className="fb-palette-item"
              data-module-id={mod.moduleId}
              data-module-name={mod.name}
              draggable={!disabled}
              onDragStart={(e) => {
                if (disabled) {
                  e.preventDefault();
                  return;
                }
                onDragStart(e, mod);
              }}
              title={mod.description ?? mod.name}
            >
              <span
                className="fb-palette-dot"
                style={{ background: mod.uiColor || '#94A3B8' }}
              />
              <span className="fb-palette-name">{mod.name}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function PaletteHeader({ onCollapse }: { onCollapse?: () => void }) {
  return (
    <div className="fb-palette-hd">
      <span className="fb-palette-hd-title">Modules</span>
      {onCollapse && (
        <button
          type="button"
          className="fb-palette-collapse"
          onClick={onCollapse}
          title="Collapse modules palette"
          aria-label="Collapse modules palette"
        >
          ‹
        </button>
      )}
    </div>
  );
}
